import {
  BarChartOutlined,
  CalculatorOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  DiffOutlined,
  FileDoneOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Card, Form, notification, Space, Spin, Steps } from "antd";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import UploadStep from "./steps/UploadStep";
import {
  HistoricalDataFileResponse,
  HistoricalDataFilesStatusResponse,
} from "api/generated";
import HistoricalDataUploadConfigurations from "./steps/HistoricalDataUploadConfigurations";
import LayoutValidationsStep from "./steps/LayoutValidationsStep";
import {
  HISTORICAL_DATA_TYPE,
  UPLOAD_KIND,
  useHistoricalData,
} from "hooks/historicalData/useHistoricalData";
import SummaryViewStep from "./steps/SummaryViewStep";
import { useDateRange } from "context/DateRangeContext";
import MigrationStep from "./steps/MigrationStep";
import { isAxiosError } from "axios";
import GenerateJsonFiles from "./steps/GenerateJsonFiles";

enum UploadSteps {
  UPLOAD_STEP,
  VALIDATE_STEP,
  SUMMARY_STEP,
  MIGRATION_STEP,
  JSON_STEP,
}

interface StepsProps {
  title: string;
  key: UploadSteps;
  status: "wait" | "process" | "finish";
  disabled: boolean;
  icon: ReactNode;
}

function UploadHistoricalData() {
  const {
    mapBackendTypeToFrontend,
    uploadFile: uploadFileToBackend,
    checkFileStatus,
    downloadLayoutFile,
    allFilesUploaded,
    formatLocaleYear,
    formatLocaleMonth,
    validateLayoutFiles,
    downloadHistoricalDataFile,
    deleteAndMigrate,
    deleteFile,
    createJsonFiles,
  } = useHistoricalData();
  const { rangeTimeValue, handleMonthChange } = useDateRange();

  // GLOBAL STATE
  const [loading, setLoading] = useState(false);
  const [kindSelected, setKindSelected] = useState(UPLOAD_KIND.PLANTAS);
  const [currentStep, setCurrentStep] = useState<UploadSteps>(
    UploadSteps.UPLOAD_STEP
  );
  const [filesStatus, setFilesStatus] =
    useState<HistoricalDataFilesStatusResponse>();

  // UPLOAD_STEP STATE
  const existingFiles: HistoricalDataFileResponse[] = useMemo(() => {
    return filesStatus?.historicalFiles ?? [];
  }, [filesStatus]);

  const areAllFilesUploaded = useMemo(
    () => allFilesUploaded(existingFiles, kindSelected),
    [existingFiles, kindSelected, allFilesUploaded]
  );

  // VALIDATE_STEP STATE
  const [validationLoading, setValidationLoading] = useState(false);
  const [summaryFile, setSummaryFile] = useState<string[]>();

  const validationLogs: string = useMemo(() => {
    return filesStatus?.logs ?? "";
  }, [filesStatus]);

  const isAnyErrorOnValidations: boolean = useMemo(() => {
    if (validationLogs.length === 0) {
      return true;
    }
    return validationLogs.toLowerCase().includes("error");
  }, [validationLogs]);

  // SUMMARY_STEP STATE
  const [errorsOnSummary, setErrorsOnSummary] = useState(false);

  // MIGRATION STATE
  const cleanupLogs: string = useMemo(() => {
    return filesStatus?.cleanupLogs ?? "";
  }, [filesStatus]);
  const insertLogs: string = useMemo(() => {
    return filesStatus?.insertLogs ?? "";
  }, [filesStatus]);
  const [wasExecuted, setWasExecuted] = useState(false);

  // JSON STATE
  const [jsonSummaryFile, setJsonSummaryFile] = useState<string[]>();
  const [differencesFile, setDifferencesFile] = useState<string[]>();

  const selectedMonth = useMemo(() => {
    return rangeTimeValue[0];
  }, [rangeTimeValue]);
  const localeYear = useMemo(
    () => (selectedMonth ? formatLocaleYear(selectedMonth) : ""),
    [selectedMonth, formatLocaleYear]
  );
  const localeMonth = useMemo(
    () => (selectedMonth ? formatLocaleMonth(selectedMonth) : ""),
    [selectedMonth, formatLocaleMonth]
  );

  function isStepDisable(step: UploadSteps): boolean {
    switch (step) {
      case UploadSteps.UPLOAD_STEP:
        return false;
      case UploadSteps.VALIDATE_STEP:
        return !areAllFilesUploaded;
      case UploadSteps.SUMMARY_STEP:
        return isAnyErrorOnValidations;
      case UploadSteps.MIGRATION_STEP:
        return (
          errorsOnSummary || isAnyErrorOnValidations || !areAllFilesUploaded
        );
      case UploadSteps.JSON_STEP:
        return false;
      default:
        return true;
    }
  }

  function getStepStatus(step: UploadSteps): "wait" | "process" | "finish" {
    switch (step) {
      case UploadSteps.UPLOAD_STEP:
        return areAllFilesUploaded ? "finish" : "process";
      case UploadSteps.VALIDATE_STEP:
        return areAllFilesUploaded ? "finish" : "wait";
      case UploadSteps.SUMMARY_STEP:
        return areAllFilesUploaded && !isAnyErrorOnValidations
          ? "finish"
          : "wait";
      case UploadSteps.MIGRATION_STEP:
        return "wait";
      default:
        return "wait";
    }
  }

  const steps: StepsProps[] = useMemo(() => {
    return [
      {
        title: "Carga",
        key: UploadSteps.UPLOAD_STEP,
        status: getStepStatus(UploadSteps.UPLOAD_STEP),
        disabled: isStepDisable(UploadSteps.UPLOAD_STEP),
        icon: areAllFilesUploaded ? (
          <FileDoneOutlined />
        ) : (
          <CloudUploadOutlined />
        ),
      },
      {
        title: "Validaciones",
        key: UploadSteps.VALIDATE_STEP,
        status: getStepStatus(UploadSteps.VALIDATE_STEP),

        disabled: isStepDisable(UploadSteps.VALIDATE_STEP),
        icon: <BarChartOutlined />,
      },
      {
        title: "Resumen",
        key: UploadSteps.SUMMARY_STEP,
        status: getStepStatus(UploadSteps.SUMMARY_STEP),
        disabled: isStepDisable(UploadSteps.SUMMARY_STEP),
        icon: <CalculatorOutlined />,
      },
      {
        title: "Migración",
        key: UploadSteps.MIGRATION_STEP,
        status:
          currentStep > 1 ? (errorsOnSummary ? "process" : "finish") : "wait",
        disabled: isStepDisable(UploadSteps.MIGRATION_STEP),
        icon: <DatabaseOutlined />,
      },
      {
        title: "JSON",
        key: UploadSteps.JSON_STEP,
        status: "wait",
        disabled: isStepDisable(UploadSteps.JSON_STEP),
        icon: <DiffOutlined />,
      },
    ];
  }, [areAllFilesUploaded, currentStep]);

  const uploadFile = useCallback(
    async (fileContent: string, label: HISTORICAL_DATA_TYPE) => {
      setLoading(true);
      try {
        const data = await uploadFileToBackend(
          fileContent,
          label,
          localeYear,
          localeMonth,
          kindSelected
        );

        if (data) {
          setFilesStatus(data);
        }
        return data;
      } catch (error) {
        notification.error({
          message: "Error al enviar el layout",
          description: `${error}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      uploadFileToBackend,
      localeYear,
      localeMonth,
      kindSelected,
      mapBackendTypeToFrontend,
      filesStatus,
    ]
  );

  const checkCurrentFileStatus = useCallback(async () => {
    if (!selectedMonth) return;

    setLoading(true);
    try {
      const data = await checkFileStatus(
        kindSelected,
        formatLocaleYear(selectedMonth),
        formatLocaleMonth(selectedMonth)
      );
      setFilesStatus(data);
    } catch (error) {
      notification.error({
        message: "Error buscar por layouts enviados",
        description: `${error}`,
      });
    } finally {
      setLoading(false);
    }
  }, [
    selectedMonth,
    kindSelected,
    checkFileStatus,
    formatLocaleYear,
    formatLocaleMonth,
  ]);

  const executeValidations = useCallback(
    async (moveToValidateStep: boolean) => {
      if (!selectedMonth) return;
      setValidationLoading(true);
      try {
        const data = await validateLayoutFiles(
          kindSelected,
          formatLocaleYear(selectedMonth),
          formatLocaleMonth(selectedMonth)
        );
        setFilesStatus(data);
        if (moveToValidateStep) {
          setCurrentStep(UploadSteps.VALIDATE_STEP);
        }
        return data?.logs ?? "";
      } catch (error) {
        let description = `${error}`;
        if (isAxiosError(error)) {
          description = error.response?.data.message ?? description;
        }
        notification.error({
          message: "Error al ejecutar el proceso de validación",
          description,
        });
      } finally {
        setValidationLoading(false);
      }
    },
    [
      selectedMonth,
      kindSelected,
      checkFileStatus,
      formatLocaleYear,
      formatLocaleMonth,
    ]
  );

  const downloadHistoricalLayoutFile = useCallback(
    (label: HISTORICAL_DATA_TYPE) => {
      if (!selectedMonth) return;

      setLoading(true);
      downloadLayoutFile(label, localeYear, localeMonth, kindSelected)
        .catch((error) => {
          notification.error({
            message: "Error al descargar el layout",
            description: `${error}`,
          });
        })
        .finally(() => setLoading(false));
    },
    [selectedMonth, downloadLayoutFile, localeYear, localeMonth, kindSelected]
  );

  const fetchHistoricalDataFile = useCallback(
    async (type: HISTORICAL_DATA_TYPE) => {
      if (!selectedMonth) return;

      setLoading(true);
      try {
        const blobData = await downloadHistoricalDataFile(
          kindSelected,
          localeYear,
          localeMonth,
          type
        );
        switch (type) {
          case HISTORICAL_DATA_TYPE.PRE_VALIDATION_SUMMARY:
            setSummaryFile(blobData);
            break;
          case HISTORICAL_DATA_TYPE.DIFFERENCES_FILE:
            setDifferencesFile(blobData);
            break;
          case HISTORICAL_DATA_TYPE.JSONS_SUMMARY:
            setJsonSummaryFile(blobData);
            break;

          default:
            notification.error({
              message: "No esta soportada la presentación de este archivo",
              description: `Actualmente no hay ninguna tabla para presentar el tipo ${type}`,
            });
            break;
        }
      } catch (error) {
        notification.error({
          message: `Error al cargar el resumen de tipo ${type}}`,
          description: `${error}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      selectedMonth,
      kindSelected,
      localeYear,
      localeMonth,
      downloadHistoricalDataFile,
    ]
  );

  const executeMigration = useCallback(
    async (killDatabase: boolean) => {
      if (!selectedMonth) return;
      setValidationLoading(true);
      try {
        const data = await deleteAndMigrate(
          kindSelected,
          formatLocaleYear(selectedMonth),
          formatLocaleMonth(selectedMonth),
          killDatabase
        );
        setFilesStatus(data);
        setWasExecuted(killDatabase);
      } catch (error) {
        let description = `${error}`;
        if (isAxiosError(error)) {
          description = error.response?.data.message ?? description;
        }
        notification.error({
          message: "Error al ejecutar el proceso de eliminacion",
          description,
        });
      } finally {
        setValidationLoading(false);
      }
    },
    [
      selectedMonth,
      kindSelected,
      checkFileStatus,
      formatLocaleYear,
      formatLocaleMonth,
    ]
  );

  const deleteLayoutFile = useCallback(
    (label: HISTORICAL_DATA_TYPE) => {
      if (!selectedMonth) return;

      setLoading(true);
      deleteFile(label, localeYear, localeMonth, kindSelected)
        .then((data) => {
          setFilesStatus(data);
        })
        .catch((error) => {
          notification.error({
            message: "Error al eliminar layouts enviados",
            description: `${error}`,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      selectedMonth,
      kindSelected,
      checkFileStatus,
      formatLocaleYear,
      formatLocaleMonth,
    ]
  );

  const executeJsonCreation = useCallback(async () => {
    if (!selectedMonth) return;
    setLoading(true);
    try {
      const data = await createJsonFiles(
        kindSelected,
        formatLocaleYear(selectedMonth),
        formatLocaleMonth(selectedMonth)
      );
      setFilesStatus(data);
    } catch (error) {
      let description = `${error}`;
      if (isAxiosError(error)) {
        description = error.response?.data.message ?? description;
      }
      notification.error({
        message:
          "Error al ejecutar el proceso de construccion de reportes Json",
        description,
      });
    } finally {
      setLoading(false);
      setJsonSummaryFile(undefined);
      setDifferencesFile(undefined);
    }
  }, [
    selectedMonth,
    kindSelected,
    checkFileStatus,
    formatLocaleYear,
    formatLocaleMonth,
  ]);

  useEffect(() => {
    checkCurrentFileStatus();
  }, [kindSelected, selectedMonth, checkCurrentFileStatus]);

  useEffect(() => {
    if (!areAllFilesUploaded && currentStep !== UploadSteps.UPLOAD_STEP) {
      setCurrentStep(UploadSteps.UPLOAD_STEP);
    }
  }, [areAllFilesUploaded, currentStep]);

  useEffect(() => {
    setCurrentStep(UploadSteps.UPLOAD_STEP);
  }, [selectedMonth, kindSelected]);

  function navigateToStep(step: UploadSteps) {
    if (step !== UploadSteps.MIGRATION_STEP) {
      setWasExecuted(false);
    }
    if (step === UploadSteps.JSON_STEP) {
      setJsonSummaryFile(undefined);
      setDifferencesFile(undefined);
    }
    setCurrentStep(step);
  }

  return (
    <div>
      <Card
        title={
          <div>
            <Space>
              <UploadOutlined />
              Gestión de Carga Histórica de Archivos
            </Space>
            <p style={{ fontSize: "12px" }}>
              Sistema completo para carga, validación, aprobación y generacion
              de archivos JSON mensuales
            </p>
          </div>
        }
      >
        <Form layout="vertical">
          <HistoricalDataUploadConfigurations
            setKindSelected={setKindSelected}
            setSelectedMonth={handleMonthChange}
            rangeTimeValue={rangeTimeValue}
          />
          <Spin
            spinning={loading || validationLoading}
            tip={
              validationLoading
                ? "Este proceso puede demorar varios minutos ..."
                : ""
            }
          >
            <Steps
              current={currentStep}
              type="navigation"
              size="small"
              onChange={navigateToStep}
              items={steps.map((item) => {
                return {
                  title: item.title,
                  status: item.status,
                  disabled: item.disabled,
                  icon: item.icon,
                };
              })}
            />
            {currentStep === UploadSteps.UPLOAD_STEP && (
              <UploadStep
                kindSelected={kindSelected}
                selectedMonth={selectedMonth}
                areAllFilesUploaded={areAllFilesUploaded}
                handleUpload={uploadFile}
                existingFiles={existingFiles
                  .map((item) => item.type ?? "")
                  .map(mapBackendTypeToFrontend)}
                uploadedFiles={existingFiles}
                downloadHistoricalLayoutFile={downloadHistoricalLayoutFile}
                deleteLayoutFile={deleteLayoutFile}
                executeValidationsAndContinue={() => {
                  executeValidations(true);
                }}
              />
            )}
            {currentStep === UploadSteps.VALIDATE_STEP && (
              <LayoutValidationsStep
                kindSelected={kindSelected}
                currentFiles={existingFiles}
                validationLogs={validationLogs}
                executeValidations={() => {
                  executeValidations(false);
                }}
                goToPreValidationsStep={() => {
                  navigateToStep(UploadSteps.SUMMARY_STEP);
                }}
              />
            )}

            {currentStep === UploadSteps.SUMMARY_STEP && (
              <SummaryViewStep
                downloadSummaryFile={fetchHistoricalDataFile}
                summaryFile={
                  summaryFile ? (summaryFile as unknown as Blob) : undefined
                }
                loading={loading}
                downloadHistoricalLayoutFile={downloadHistoricalLayoutFile}
                setErrorsOnSummary={setErrorsOnSummary}
                kindSelected={kindSelected}
                goToMigrateDatabase={() => {
                  navigateToStep(UploadSteps.MIGRATION_STEP);
                }}
              />
            )}

            {currentStep === UploadSteps.MIGRATION_STEP && (
              <MigrationStep
                executeMigration={(killDatabase) => {
                  executeMigration(killDatabase);
                }}
                validationLogs={validationLogs}
                insertLogs={insertLogs}
                cleanupLogs={cleanupLogs}
                loading={validationLoading}
                wasExecuted={wasExecuted}
              />
            )}
            {currentStep === UploadSteps.JSON_STEP && (
              <GenerateJsonFiles
                executeProcess={executeJsonCreation}
                downloadHistoricalLayoutFile={downloadHistoricalLayoutFile}
                fetchFile={(type) => {
                  fetchHistoricalDataFile(type);
                }}
                currentFiles={existingFiles}
                filesStatus={filesStatus}
                loading={loading}
                jsonSummaryFile={
                  jsonSummaryFile
                    ? (jsonSummaryFile as unknown as Blob)
                    : undefined
                }
                differencesFile={
                  differencesFile
                    ? (differencesFile as unknown as Blob)
                    : undefined
                }
              />
            )}
          </Spin>
        </Form>
      </Card>
    </div>
  );
}

export default UploadHistoricalData;
